import { openDocumentOnClick } from '../helper/documentHelper';
import { withQueryParam } from '../helper/urlHelper';
import React, { useState } from 'react';

import { Space, Row, message, Tabs, Typography, Badge, Flex } from 'antd';
import OrganizationComponent from './CollaborationSigning/OrganizationComponent';
import { useTranslation } from 'react-i18next';
import {
  GetOrganizationRegistrations,
  SignRegistrationByLegalDepartment,
  SignRegistrationByRector,
} from '../helper/apiHelper';
import type {
  OrganizationRegistration,
  ReducedOrganization,
} from '../Types/interfaces';
import { Roles } from '../Types/interfaces';
import FileUploader from './FileUploader';
import OrganizationRegistrationForm from './Organization/OrganizationRegistrationForm';
import { useCurrentUser } from '../hooks/useCurrentUser';
import {
  useSignedRegistrations,
  useUnregisteredOrganizations,
  useUnsignedRegistrations,
} from '../hooks/BadgeHooks/LegalDepartmentHooks';
import { QUERY_KEYS } from '../Constants/queryKeys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUserRoles } from '../hooks/useHasRole';
import { AllRegistrationsTable } from './LegalDepartment/AllRegistrationsTable';
import { StudyYearDropdown } from './StudyYearDropdown';
import { collaborationDocumentStatus } from '../Utils/organizationProcessUtils';
import { NoData } from './NoData';
import unsignedDocumentImg from '../Assets/Images/signedDocument.png';
import unsignedDocumentImgLight from '../Assets/Images/signedDocumentLight.png';
import { useTheme } from '../context/ThemeContext';

type SigningMode = 'legal' | 'rector';

interface CollaborationSigningComponentProps {
  signingMode: SigningMode;
}

const CollaborationSigningComponent = ({
  signingMode,
}: CollaborationSigningComponentProps): React.ReactNode => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [, contextHolder] = message.useMessage();
  const loggedInUser = useCurrentUser();
  const { isDarkMode } = useTheme();

  const getStepName = (): string => {
    switch (signingMode) {
      case 'legal':
        return 'SigningOrganizationRegistrationDocumentByLegalDepartment';
      case 'rector':
        return 'SigningCollaborationDocumentByRector';
      default:
        return '';
    }
  };

  const currentUserRoles = useCurrentUserRoles();
  const canSeeRegistrations = currentUserRoles.some(
    (role) =>
      role === Roles.Administrator ||
      role === Roles.DepartmentRepresentative ||
      role === Roles.Rector ||
      role === Roles.LegalDepartment
  );
  const { data: organizationRegistrations = [] } = useQuery<
    Array<OrganizationRegistration>
  >({
    queryKey: [...QUERY_KEYS.ORGANIZATION_REGISTRATIONS],
    queryFn: () => GetOrganizationRegistrations(),
    enabled: canSeeRegistrations,
  });

  // undefined until the user selects a study year, then the backend returns the current one
  // and the dropdown shows the current one
  const [selectedStudyYearId, setSelectedStudyYearId] = useState<number>();
  // the registrations listed in the unsigned and signed tabs; the counts of the tabs stay on the current study year
  const { data: studyYearRegistrations = [] } = useQuery<
    Array<OrganizationRegistration>
  >({
    queryKey:
      selectedStudyYearId === undefined
        ? [...QUERY_KEYS.ORGANIZATION_REGISTRATIONS]
        : [
            ...QUERY_KEYS.ORGANIZATION_REGISTRATIONS,
            'studyYear',
            selectedStudyYearId,
          ],
    queryFn: () => GetOrganizationRegistrations(selectedStudyYearId),
    enabled: canSeeRegistrations,
  });

  const unregisteredOrganizations = useUnregisteredOrganizations(
    organizationRegistrations,
    getStepName()
  );
  const unsignedRegistrations = useUnsignedRegistrations(
    organizationRegistrations,
    getStepName()
  );
  const signedRegistrations = useSignedRegistrations(
    organizationRegistrations,
    getStepName()
  );
  const shownUnsignedRegistrations = useUnsignedRegistrations(
    studyYearRegistrations,
    getStepName()
  );
  const shownSignedRegistrations = useSignedRegistrations(
    studyYearRegistrations,
    getStepName()
  );

  const studyYearFilter = (
    <Flex
      align="center"
      justify="start"
      gap={8}
      wrap
      style={{ marginBottom: 10 }}
    >
      <Typography.Text strong>{t('studyYear.label')}:</Typography.Text>
      <StudyYearDropdown
        value={selectedStudyYearId}
        onChange={(studyYearId: number): void =>
          setSelectedStudyYearId(studyYearId)
        }
      />
    </Flex>
  );

  const getTranslationPrefix = (): string => {
    switch (signingMode) {
      case 'legal':
        return 'legDepComp';
      case 'rector':
        return 'rectorComp';
      default:
        return 'legDepComp';
    }
  };

  function mapToReducedOrganization(
    orgReg: OrganizationRegistration
  ): ReducedOrganization {
    const reducedOrg: ReducedOrganization = {
      id: orgReg.organizationId,
      name: orgReg.organizationName,
      isUniversityOrganization: false,
    };
    return reducedOrg;
  }

  const canUpload = (): boolean => {
    if (!loggedInUser) return false;
    // Both legal department and rector can upload files
    return (
      (loggedInUser.roles & Roles.LegalDepartment) !== 0 ||
      (loggedInUser.roles & Roles.Rector) !== 0
    );
  };

  const translationPrefix = getTranslationPrefix();

  const onRegistration = async (): Promise<void> => {
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.ORGANIZATION_REGISTRATIONS,
    });
  };

  return (
    <>
      {contextHolder}
      <Tabs centered type="card" defaultActiveKey="2">
        {signingMode === 'legal' ? (
          <Tabs.TabPane
            tab={
              <span>
                {t(`legDepComp.waitingForRegistration`)}
                <Badge
                  count={unregisteredOrganizations.length}
                  overflowCount={10}
                  size={'small'}
                />
              </span>
            }
            key="1"
          >
            {unregisteredOrganizations &&
            unregisteredOrganizations.length > 0 ? (
              <Tabs tabPosition="left">
                {unregisteredOrganizations.map((reg) => {
                  const org: ReducedOrganization =
                    mapToReducedOrganization(reg);
                  return (
                    <Tabs.TabPane key={reg.id} tab={<Space>{org.name}</Space>}>
                      <OrganizationComponent
                        organization={org}
                      ></OrganizationComponent>
                      <Row justify="center" style={{ paddingBottom: 7 }}>
                        <Space direction="vertical" align="center">
                          <Space align="center">
                            <Typography.Link
                              href={withQueryParam(
                                reg.collaborationDocument.url,
                                'status',
                                collaborationDocumentStatus(reg.processSteps)
                              )}
                              onClick={openDocumentOnClick(
                                reg.collaborationDocument.url,
                                collaborationDocumentStatus(reg.processSteps)
                              )}
                              target="_blank"
                            >
                              {t(`${translationPrefix}.viewDoc`)}
                            </Typography.Link>
                          </Space>
                          <OrganizationRegistrationForm
                            onRegistration={onRegistration}
                            registrationId={reg.id}
                          />
                        </Space>
                      </Row>
                    </Tabs.TabPane>
                  );
                })}
              </Tabs>
            ) : (
              <NoData
                message={t('legDepOrgComp.noPendingRegistrations')}
                imgSource={
                  isDarkMode ? unsignedDocumentImgLight : unsignedDocumentImg
                }
              />
            )}
          </Tabs.TabPane>
        ) : null}

        <Tabs.TabPane
          tab={
            <span>
              {t(`${translationPrefix}.unsignedRegs`)}
              <Badge
                count={unsignedRegistrations.length}
                overflowCount={10}
                size={'small'}
              />
            </span>
          }
          key="2"
          style={{ marginBottom: 20 }}
        >
          {studyYearFilter}
          {shownUnsignedRegistrations.length > 0 ? (
            // a new list for every study year, so the selected organization is not kept
            <Tabs key={selectedStudyYearId} tabPosition="left">
              {shownUnsignedRegistrations.map((reg) => {
                const org: ReducedOrganization = mapToReducedOrganization(reg);
                return (
                  <Tabs.TabPane key={reg.id} tab={<Space>{org.name}</Space>}>
                    <OrganizationComponent
                      organization={org}
                    ></OrganizationComponent>
                    <Row justify="center" style={{ paddingBottom: 7 }}>
                      <Typography.Link
                        href={withQueryParam(
                          reg.collaborationDocument.url,
                          'status',
                          collaborationDocumentStatus(reg.processSteps)
                        )}
                        onClick={openDocumentOnClick(
                          reg.collaborationDocument.url,
                          collaborationDocumentStatus(reg.processSteps)
                        )}
                        target="_blank"
                      >
                        {t(`${translationPrefix}.viewDoc`)}
                      </Typography.Link>
                    </Row>
                    <Row justify="center">
                      {canUpload() && signingMode === 'rector' ? (
                        <FileUploader
                          role={loggedInUser.roles}
                          apiUrl={'collaboration-documents'}
                          documentUrl={reg.collaborationDocument.url}
                          documentSigningStatus={collaborationDocumentStatus(
                            reg.processSteps
                          )}
                          isSignatureDisabled={false}
                          alreadySigned={false}
                          onFileUpload={async () => {
                            // After successful upload, mark the rector signing step as completed
                            await SignRegistrationByRector(reg.id.toString());
                            // Refresh the data to move the document to signed tab
                            queryClient.invalidateQueries({
                              queryKey: QUERY_KEYS.ORGANIZATION_REGISTRATIONS,
                            });
                            queryClient.invalidateQueries({
                              queryKey:
                                QUERY_KEYS.DETAILED_ORGANIZATION_REGISTRATIONS,
                            });
                          }}
                        />
                      ) : null}
                      {canUpload() && signingMode === 'legal' ? (
                        <FileUploader
                          role={loggedInUser.roles}
                          apiUrl={'collaboration-documents'}
                          documentUrl={reg.collaborationDocument.url}
                          documentSigningStatus={collaborationDocumentStatus(
                            reg.processSteps
                          )}
                          isSignatureDisabled={false}
                          alreadySigned={false}
                          onFileUpload={async () => {
                            // After successful upload, mark the legal department signing step as completed
                            await SignRegistrationByLegalDepartment(
                              reg.id.toString()
                            );
                            // Refresh the data to move the document to signed tab
                            queryClient.invalidateQueries({
                              queryKey: QUERY_KEYS.ORGANIZATION_REGISTRATIONS,
                            });
                          }}
                        />
                      ) : null}
                    </Row>
                  </Tabs.TabPane>
                );
              })}
            </Tabs>
          ) : (
            <NoData
              imgSource={
                isDarkMode ? unsignedDocumentImgLight : unsignedDocumentImg
              }
            />
          )}
        </Tabs.TabPane>

        <Tabs.TabPane
          tab={
            <span>
              {t(`${translationPrefix}.signedRegs`)}
              <Badge
                count={signedRegistrations.length}
                overflowCount={10}
                size={'small'}
              />
            </span>
          }
          key="3"
          style={{ marginBottom: 20 }}
        >
          {studyYearFilter}
          {shownSignedRegistrations.length > 0 ? (
            // a new list for every study year, so the selected organization is not kept
            <Tabs key={selectedStudyYearId} tabPosition="left">
              {shownSignedRegistrations.map((reg) => {
                const org: ReducedOrganization = mapToReducedOrganization(reg);
                return (
                  <Tabs.TabPane key={reg.id} tab={<Space>{org.name}</Space>}>
                    <OrganizationComponent
                      organization={org}
                    ></OrganizationComponent>
                    <Row justify="center" style={{ paddingBottom: 7 }}>
                      <Space direction="vertical" align="center">
                        <Space align="center">
                          <Typography.Link
                            href={withQueryParam(
                              reg.collaborationDocument.url,
                              'status',
                              collaborationDocumentStatus(reg.processSteps)
                            )}
                            onClick={openDocumentOnClick(
                              reg.collaborationDocument.url,
                              collaborationDocumentStatus(reg.processSteps)
                            )}
                            target="_blank"
                          >
                            {t(`${translationPrefix}.viewDoc`)}
                          </Typography.Link>
                        </Space>
                      </Space>
                    </Row>
                  </Tabs.TabPane>
                );
              })}
            </Tabs>
          ) : (
            <NoData
              imgSource={
                isDarkMode ? unsignedDocumentImgLight : unsignedDocumentImg
              }
            />
          )}
        </Tabs.TabPane>

        {/* All registration  */}
        <Tabs.TabPane
          tab={<span>{t('legDepOrgComp.allRegistrations')}</span>}
          key="4"
        >
          <AllRegistrationsTable filterByStudyYear={true} />
        </Tabs.TabPane>
      </Tabs>
    </>
  );
};

export default CollaborationSigningComponent;
